import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import Drawer from 'devextreme-react/drawer';
import { Template } from 'devextreme-react/core/template';
import { triggerResizeEvent, triggerShownEvent } from 'devextreme/events/visibility_change';
import { ButtonTypes } from 'devextreme-react/button';
import { TreeViewTypes } from 'devextreme-react/tree-view';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppHeader, SideNavigationMenu, AppFooter } from '../../components';
import { useScreenSize } from '../../utils/media-query';
import { useMenuPatch } from '../../utils/patches';

import type { SideNavToolbarProps } from '../../types';

import './side-nav-outer-toolbar.scss';
import { SideNavigationItem } from '../../components/library/side-navigation-menu/SideNavigationMenu';
import { useNavigation } from '../../contexts/navigation';
import { getNavigation } from '../../app-navigation';

enum MenuOpenState {
  Closed = 1,
  Opened = 2,
  TemporaryOpened = 3,
}

type MenuStatus = MenuOpenState | null;
type OpenTab = { id: string; path: string; title: string };
type StoredMdiState = {
  tabs: OpenTab[];
  activeTabId: string;
  splitTabId: string | null;
  splitOwnedTabId: string | null;
};

const DEFAULT_TAB_PATH = '/support/dashboard';
const DEFAULT_TAB_TITLE = 'Dashboard';
const ENABLE_SPLIT_VIEW = true;
const MDI_STORAGE_KEY = 'mobisupport_tabbed_mdi_v1';

const createTabId = () => `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const isValidStoredTab = (tab: any): tab is OpenTab => {
  return tab && typeof tab.id === 'string' && typeof tab.path === 'string' && typeof tab.title === 'string';
};

const MemoizedTabPane = React.memo(
  ({ tabId, path, children: routeTemplate }: { tabId: string; path: string; children: React.ReactNode[] }) => {
    return (
      <>
        {routeTemplate.map((item) =>
          React.isValidElement(item)
            ? React.cloneElement(item as React.ReactElement<any>, {
              location: path,
              key: `${tabId}-${String(item.key ?? 'routes')}`,
            })
            : item
        )}
      </>
    );
  },
  (prev, next) => prev.tabId === next.tabId && prev.path === next.path
);

export const SideNavOuterToolbar = ({ title, children }: React.PropsWithChildren<SideNavToolbarProps>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setNavigationData } = useNavigation();
  const { t } = useTranslation();
  const { isXSmall, isLarge } = useScreenSize();


  const pathIconMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const group of getNavigation(t)) {
      if (group.icon && group.items) {
        for (const item of group.items) {
          if (item.path) map[item.path] = group.icon;
        }
      }
    }
    return map;
  }, [t]);
  const [patchCssClass, onMenuReady] = useMenuPatch();
  const [menuStatus, setMenuStatus] = useState<MenuStatus>(null);
  const stableChildrenRef = useRef<React.ReactNode[] | null>(null);
  const navigationTargetTabIdRef = useRef<string | null>(null);

  const getInitialState = useCallback((): StoredMdiState => {
    const fallbackTabId = createTabId();
    const fallback: StoredMdiState = {
      tabs: [{ id: fallbackTabId, path: location.pathname || DEFAULT_TAB_PATH, title: title || DEFAULT_TAB_TITLE }],
      activeTabId: fallbackTabId,
      splitTabId: null,
      splitOwnedTabId: null,
    };

    try {
      const raw = localStorage.getItem(MDI_STORAGE_KEY);
      if (!raw) {
        return fallback;
      }

      const parsed = JSON.parse(raw) as StoredMdiState;
      const tabs = Array.isArray(parsed?.tabs) ? parsed.tabs.filter(isValidStoredTab) : [];
      if (!tabs.length) {
        return fallback;
      }

      const activeTabId = tabs.some((tab) => tab.id === parsed.activeTabId)
        ? parsed.activeTabId
        : tabs[0].id;
      const splitTabId = parsed.splitTabId && tabs.some((tab) => tab.id === parsed.splitTabId)
        ? parsed.splitTabId
        : null;
      const splitOwnedTabId = parsed.splitOwnedTabId && tabs.some((tab) => tab.id === parsed.splitOwnedTabId)
        ? parsed.splitOwnedTabId
        : null;

      return { tabs, activeTabId, splitTabId, splitOwnedTabId };
    } catch (err) {
      return fallback;
    }
  }, [location.pathname, title]);

  const [{ tabs, activeTabId, splitTabId, splitOwnedTabId }, setMdiState] = useState<StoredMdiState>(getInitialState);

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTabId) || tabs[0] || null,
    [tabs, activeTabId]
  );
  const splitTab = useMemo(
    () => splitTabId ? tabs.find((tab) => tab.id === splitTabId) || null : null,
    [tabs, splitTabId]
  );

  const getDefaultMenuOpenState = useCallback(() => isLarge ? MenuOpenState.Opened : MenuOpenState.Closed, [isLarge]);
  const getMenuOpenState = useCallback((status: MenuStatus) => {
    if (status === null) {
      return getDefaultMenuOpenState();
    }

    return status;
  }, [getDefaultMenuOpenState]);

  const getMenuStatus = useCallback((status: MenuStatus) => {
    return status === getDefaultMenuOpenState() ? null : status;
  }, [getDefaultMenuOpenState]);

  const changeMenuStatus = useCallback((reducerFn: (prevStatus: MenuStatus) => MenuStatus) => {
    setMenuStatus(prevMenuStatus => getMenuStatus(reducerFn(getMenuOpenState(prevMenuStatus)) ?? prevMenuStatus));
  }, [getMenuOpenState, getMenuStatus]);

  const toggleMenu = useCallback(({ event }: ButtonTypes.ClickEvent) => {
    changeMenuStatus(prevStatus => prevStatus === MenuOpenState.Closed ? MenuOpenState.Opened : MenuOpenState.Closed);
    event?.stopPropagation();
  }, [changeMenuStatus]);

  const temporaryOpenMenu = useCallback(() => {
    changeMenuStatus(prevStatus => prevStatus === MenuOpenState.Closed ? MenuOpenState.TemporaryOpened : null);
  }, [changeMenuStatus]);

  const onOutsideClick = useCallback(() => {
    changeMenuStatus(prevStatus => prevStatus !== MenuOpenState.Closed && !isLarge ? MenuOpenState.Closed : null);
    return !isLarge;
  }, [isLarge, changeMenuStatus]);

  useEffect(() => {
    changeMenuStatus(() => menuStatus);
  }, [isLarge]);

  useEffect(() => {
    try {
      localStorage.setItem(
        MDI_STORAGE_KEY,
        JSON.stringify({ tabs, activeTabId, splitTabId, splitOwnedTabId })
      );
    } catch (err) {
      // ignore localStorage failures
    }
  }, [tabs, activeTabId, splitTabId, splitOwnedTabId]);

  useEffect(() => {
    if (!activeTab) {
      return;
    }

    setNavigationData?.({ currentPath: activeTab.path });
  }, [activeTab?.path, setNavigationData]);

  useEffect(() => {
    const targetId = navigationTargetTabIdRef.current;
    if (targetId) {
      navigationTargetTabIdRef.current = null;
      setMdiState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === targetId
            ? { ...tab, path: location.pathname, title: title || tab.title || DEFAULT_TAB_TITLE }
            : tab
        ),
      }));
      return;
    }

    // Sayfa-ici navigate() (orn. "Yeni Ticket" butonu): hedef tab belirtilmemis olsa
    // da URL aktif tab'in path'inden farkliysa aktif tab'i yeni path'e guncelle ki
    // tab icerigi (Routes location=tab.path) yeni sayfayi gostersin.
    setMdiState((prev) => {
      const active = prev.tabs.find((tab) => tab.id === prev.activeTabId);
      if (!active || active.path === location.pathname) return prev;
      return {
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === prev.activeTabId
            ? { ...tab, path: location.pathname, title: title || tab.title || DEFAULT_TAB_TITLE }
            : tab
        ),
      };
    });
  }, [location.pathname, title]);

  const onNavigationChanged = useCallback(
    ({ itemData: { path }, event, node }: TreeViewTypes.ItemClickEvent & { itemData: SideNavigationItem }) => {
      if (getMenuOpenState(menuStatus) === MenuOpenState.Closed || !path || node?.selected || !activeTab) {
        event?.preventDefault();
        return;
      }

      navigationTargetTabIdRef.current = activeTab.id;
      setMdiState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === prev.activeTabId ? { ...tab, path } : tab
        ),
      }));
      navigate(path);

      if (!isLarge || menuStatus === MenuOpenState.TemporaryOpened) {
        setMenuStatus(getMenuStatus(MenuOpenState.Closed));
        event?.stopPropagation();
      }
    },
    [navigate, menuStatus, isLarge, activeTab, getMenuOpenState, getMenuStatus]
  ) as (e: TreeViewTypes.ItemClickEvent) => void;

  const addTab = useCallback(() => {
    const tabId = createTabId();
    setMdiState((prev) => ({
      ...prev,
      tabs: [
        ...prev.tabs,
        {
          id: tabId,
          path: DEFAULT_TAB_PATH,
          title: DEFAULT_TAB_TITLE,
        },
      ],
      activeTabId: tabId,
      splitTabId: prev.splitTabId === tabId ? null : prev.splitTabId,
      splitOwnedTabId: prev.splitOwnedTabId === tabId ? null : prev.splitOwnedTabId,
    }));
    navigationTargetTabIdRef.current = tabId;
  }, []);

  const openInNewTab = useCallback((path: string, tabTitle?: string) => {
    if (!path) {
      return;
    }

    const tabId = createTabId();
    setMdiState((prev) => ({
      ...prev,
      tabs: [...prev.tabs, { id: tabId, path, title: tabTitle || DEFAULT_TAB_TITLE }],
      activeTabId: tabId,
    }));
    navigationTargetTabIdRef.current = tabId;
    navigate(path);
  }, [navigate]);

  const selectTab = useCallback((tabId: string) => {
    if (!tabs.some((tab) => tab.id === tabId)) {
      return;
    }

    setMdiState((prev) => ({ ...prev, activeTabId: tabId }));
  }, [tabs]);

  const closeTab = useCallback((tabId: string) => {
    setMdiState((prev) => {
      if (prev.tabs.length <= 1) {
        return prev;
      }

      const closingIndex = prev.tabs.findIndex((tab) => tab.id === tabId);
      if (closingIndex === -1) {
        return prev;
      }

      const nextTabs = prev.tabs.filter((tab) => tab.id !== tabId);
      const nextSplit = prev.splitTabId === tabId ? null : prev.splitTabId;
      const nextSplitOwned = prev.splitOwnedTabId === tabId ? null : prev.splitOwnedTabId;

      let nextActive = prev.activeTabId;
      if (prev.activeTabId === tabId) {
        const fallback = nextTabs[Math.max(0, closingIndex - 1)] || nextTabs[0];
        nextActive = fallback.id;
      }

      return {
        tabs: nextTabs,
        activeTabId: nextActive,
        splitTabId: nextSplit,
        splitOwnedTabId: nextSplitOwned,
      };
    });
  }, []);

  const openInSplitView = useCallback((path: string, tabTitle?: string) => {
    if (!ENABLE_SPLIT_VIEW || !path) {
      return;
    }

    setMdiState((prev) => {
      const existing = prev.tabs.find((tab) => tab.path === path);
      if (existing) {
        if (existing.id === prev.activeTabId) {
          return prev;
        }
        return { ...prev, splitTabId: existing.id, splitOwnedTabId: null };
      }

      const tabId = createTabId();
      return {
        ...prev,
        tabs: [...prev.tabs, { id: tabId, path, title: tabTitle || DEFAULT_TAB_TITLE }],
        splitTabId: tabId,
        splitOwnedTabId: tabId,
      };
    });
  }, []);

  const closeSplitPanel = useCallback((tabId: string) => {
    if (tabId !== 'split' && tabId !== splitTabId) {
      return;
    }
    setMdiState((prev) => {
      if (!prev.splitOwnedTabId) {
        return { ...prev, splitTabId: null };
      }

      const nextTabs = prev.tabs.filter((tab) => tab.id !== prev.splitOwnedTabId);
      const nextActive = prev.activeTabId === prev.splitOwnedTabId
        ? (nextTabs[0]?.id || prev.activeTabId)
        : prev.activeTabId;

      return {
        tabs: nextTabs,
        activeTabId: nextActive,
        splitTabId: null,
        splitOwnedTabId: null,
      };
    });
  }, [splitTabId]);

  const [splitPercent, setSplitPercent] = useState(50);
  const splitDragging = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Tab'lar MDI mantigiyla mount kaliyor; aktif olmayan tab `display:none` (0 boyut).
  // Bu yuzden gizliyken olculen DevExtreme widget'lari (Splitter, DataGrid) panelleri
  // tepeye yigip kaliyor. Tab gorunur olunca dx'e "shown/resize" tetikleyip yeniden
  // hesaplatiyoruz - boylece Splitter yapilandirilmis (% 50) boyutlarina geri doner.
  const notifyActivePanelsVisible = useCallback(() => {
    const panels = contentRef.current?.querySelectorAll<HTMLElement>(
      '.tab-panel.is-active, .tab-panel.is-split'
    );
    panels?.forEach((el) => {
      triggerShownEvent(el);
      triggerResizeEvent(el);
    });
  }, []);

  // Tab degisiminde: yeni aktif panel'e yeniden olcum tetikle.
  useEffect(() => {
    const raf = requestAnimationFrame(notifyActivePanelsVisible);
    return () => cancelAnimationFrame(raf);
  }, [activeTabId, splitTabId, notifyActivePanelsVisible]);

  // Ilk yukleme (F5): app shell yuksekligi bir kac frame sonra oturuyor; tek rAF cok
  // erken kaliyor ve Splitter 0 yukseklikte olculup tepeye yigiliyor. ResizeObserver
  // konteyner 0 -> gercek yukseklige gectigi ani (ve sonraki tum boyut degisimlerini)
  // yakalayip dx widget'larina yeniden hesaplatir.
  useEffect(() => {
    const wrapper = contentRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(notifyActivePanelsVisible);
    });
    ro.observe(wrapper);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [notifyActivePanelsVisible]);

  const onResizeHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    splitDragging.current = true;

    const onMouseMove = (ev: MouseEvent) => {
      if (!splitDragging.current || !contentRef.current) return;
      const rect = contentRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.min(80, Math.max(20, pct)));
    };

    const onMouseUp = () => {
      splitDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  if (!stableChildrenRef.current) {
    stableChildrenRef.current = React.Children.toArray(children).filter((item) => {
      return React.isValidElement(item) && item.type !== AppFooter;
    });
  }
  const renderedContent = stableChildrenRef.current;

  const renderTabContent = useCallback((tab: OpenTab) => {
    return <MemoizedTabPane tabId={tab.id} path={tab.path}>{renderedContent}</MemoizedTabPane>;
  }, [renderedContent]);

  const headerTabs = splitTab
    ? [
      ...tabs
        .filter((tab) => tab.id !== splitTab.id)
        .map((tab) => ({ id: tab.id, title: tab.title, icon: pathIconMap[tab.path] })),
      { id: 'split', title: splitTab.title, icon: pathIconMap[splitTab.path] },
    ]
    : tabs.map((tab) => ({ id: tab.id, title: tab.title, icon: pathIconMap[tab.path] }));

  return (
    <div className='side-nav-outer-toolbar'>
      <AppHeader
        className='layout-header'
        menuToggleEnabled
        toggleMenu={toggleMenu}
        title={title}
        tabs={headerTabs}
        activeTabId={activeTabId}
        onTabSelect={selectTab}
        onAddTab={addTab}
        onTabClose={(tabId) => {
          if (tabId === 'split') {
            closeSplitPanel(tabId);
            return;
          }
          closeTab(tabId);
        }}
        canCloseTabs={tabs.filter((tab) => tab.id !== splitTabId).length > 1 || Boolean(splitTab)}
        closeableTabIds={[
          ...(tabs.filter((tab) => tab.id !== splitTabId).length > 1
            ? tabs.filter((tab) => tab.id !== splitTabId).map((tab) => tab.id)
            : []),
          ...(splitTab ? ['split'] : []),
        ]}
      />
      <Drawer
        className={['drawer layout-body', patchCssClass].join(' ')}
        position='before'
        closeOnOutsideClick={onOutsideClick}
        openedStateMode={isLarge ? 'shrink' : 'overlap'}
        revealMode={isXSmall ? 'slide' : 'expand'}
        minSize={isXSmall ? 0 : 48}
        maxSize={250}
        shading={isLarge ? false : true}
        opened={getMenuOpenState(menuStatus) === MenuOpenState.Closed ? false : true}
        template='menu'
      >
        <div className='content'>
          <div
            ref={contentRef}
            className={`tab-content-wrapper ${splitTab ? 'split-mode' : ''}`}
          >
            {tabs.map((tab) => {
              const isSplit = splitTab && tab.id === splitTabId;
              const isActive = tab.id === activeTabId;
              let panelClass = 'tab-panel';
              let style: React.CSSProperties | undefined;

              if (isSplit) {
                panelClass += ' is-split';
                style = { width: `${100 - splitPercent}%` };
              } else if (isActive) {
                panelClass += ' is-active';
                style = splitTab ? { width: `${splitPercent}%` } : undefined;
              } else {
                panelClass += ' is-inactive';
              }

              return (
                <div key={tab.id} className={panelClass} style={style}>
                  {renderTabContent(tab)}
                </div>
              );
            })}
            {splitTab && (
              <div
                className='split-resize-handle'
                style={{ order: 1 }}
                onMouseDown={onResizeHandleMouseDown}
              />
            )}
          </div>
        </div>
        <Template name='menu'>
          <SideNavigationMenu
            compactMode={getMenuOpenState(menuStatus) === MenuOpenState.Closed}
            selectedItemChanged={onNavigationChanged}
            openMenu={temporaryOpenMenu}
            onMenuReady={onMenuReady}
            onOpenInNewTab={openInNewTab}
            onOpenInSplitView={ENABLE_SPLIT_VIEW ? openInSplitView : undefined}
          />
        </Template>
      </Drawer>
      <AppFooter></AppFooter>
    </div>
  );
};
